import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { axiosGateway } from "../../../../api/axios";
import useSavedItems from "../../../../authorization/useSavedItems";
import PostCard from "../../../../feed/components/HomePage/PostCard/PostCard";
import CaseCard from "../../../../Medicalcase/components/Casecard/Casecard";
import useDoctors from "../../../../authDoctor/hooks/useDoctors";
import "./DoctorSavedPanel.css";

const unwrap = (payload) => payload?.data ?? payload;

const getDoctorId = (doctor) =>
  doctor?.id ?? doctor?._id ?? doctor?.userId ?? doctor?.pgId ?? doctor?.doctorId;

const normalizeDoctorId = (id) =>
  String(id ?? "").trim().replace(/^DR_/i, "");

const getDoctorName = (doctor) => {
  if (!doctor) return "";
  return (
    doctor.fullName ||
    doctor.name ||
    doctor.displayName ||
    `${doctor.firstName || ""} ${doctor.lastName || ""}`.trim()
  );
};

export default function DoctorSavedPanel({ doctorDisplay }) {
  const { t } = useTranslation();
  const { doctors = [] } = useDoctors();
  const { items, status, error, isSaved, toggleSave, pendingKey } = useSavedItems();
  const [content, setContent] = useState({ posts: [], cases: [] });
  const [contentStatus, setContentStatus] = useState("idle");
  const [contentError, setContentError] = useState(null);

  const doctorsById = useMemo(() => {
    const map = new Map();
    doctors.forEach((doctor) => {
      const name = getDoctorName(doctor);
      const id = getDoctorId(doctor);
      if (id != null && name) map.set(String(id), name);
      if (doctor?.raw) {
        const rawId = getDoctorId(doctor.raw);
        if (rawId != null && name) map.set(String(rawId), name);
      }
    });
    return map;
  }, [doctors]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (status === "loading") return;
      if (!items.length) {
        setContent({ posts: [], cases: [] });
        setContentStatus("succeeded");
        return;
      }
      setContentStatus("loading");
      setContentError(null);
      try {
        const entries = await Promise.all(
          items.map(async (item) => {
            const path = item.targetType === "post"
              ? `/posts/${encodeURIComponent(item.targetId)}`
              : `/medical-cases/${encodeURIComponent(item.targetId)}`;
            try {
              const response = await axiosGateway.get(path);
              return { ...item, content: unwrap(response.data) };
            } catch (loadError) {
              if (item.targetType === "post" && loadError?.response?.status === 404) {
                await toggleSave("post", item.targetId);
                return null;
              }
              throw loadError;
            }
          })
        );
        if (!active) return;
        const availableEntries = entries.filter(Boolean);
        setContent({
          posts: availableEntries.filter((entry) => entry.targetType === "post"),
          cases: availableEntries.filter((entry) => entry.targetType === "medical_case"),
        });
        setContentStatus("succeeded");
      } catch (loadError) {
        if (!active) return;
        setContentError(loadError?.response?.data?.message || loadError?.message || "Failed to load saved content");
        setContentStatus("failed");
      }
    };
    load();
    return () => { active = false; };
  }, [items, status, toggleSave]);

  if (status === "loading" || contentStatus === "loading") {
    return <div className="saved-panel-state">{t("saved.loading", "Loading saved items...")}</div>;
  }

  if (error || contentError) {
    return <div className="saved-panel-state saved-panel-state--error">{String(error || contentError)}</div>;
  }

  const renderPost = (entry) => {
    const post = entry.content || {};
    const id = post.postId || post.id || post._id || entry.targetId;
    const ownerId = post.userId || post.authorId || post.doctorId;
    const doctorName =
      getDoctorName(post.author) ||
      getDoctorName(post.doctor) ||
      getDoctorName(post.user) ||
      doctorsById.get(String(ownerId)) ||
      "Doctor";
    return (
      <PostCard
        key={entry.id}
        postId={id}
        doctorName={doctorName}
        timeAgo={post.createdAt}
        content={post.content || post.description || post.text}
        image={post.imageUrl || post.photo}
        attachments={post.attachments || []}
        likes={post.likesCount ?? post.likes ?? 0}
        comments={post.commentsCount ?? post.comments ?? 0}
        profileImage={
          post.author?.avatar ||
          post.author?.avatarUrl ||
          post.author?.profileImage ||
          post.author?.photo ||
          (normalizeDoctorId(ownerId) === normalizeDoctorId(getDoctorId(doctorDisplay))
            ? doctorDisplay?.avatarUrl || doctorDisplay?.avatar
            : null)
        }
        currentUserId={ownerId}
        postOwnerId={ownerId}
        isSaved={isSaved("post", id)}
        onToggleSave={(postId) => toggleSave("post", postId)}
        isSaving={pendingKey === `post:${String(id)}`}
        visibleToDoctorsOnly={!!post.visibleToDoctorsOnly}
      />
    );
  };

  const renderCase = (entry) => {
    const medicalCase = entry.content || {};
    const id = medicalCase.id || medicalCase._id || entry.targetId;
    return (
      <CaseCard
        key={entry.id}
        caseItem={{ ...medicalCase, id, raw: medicalCase }}
        isSaved={isSaved("medical_case", id)}
        onToggleSave={toggleSave}
        isSaving={pendingKey === `medical_case:${String(id)}`}
      />
    );
  };

  return (
    <div className="saved-panel">
      <section className="saved-panel__section">
        <h2>{t("saved.posts", "Saved Posts")}</h2>
        {content.posts.length ? content.posts.map(renderPost) : <p className="saved-panel__empty">{t("saved.emptyPosts", "No saved posts")}</p>}
      </section>
      <section className="saved-panel__section">
        <h2>{t("saved.cases", "Saved Medical Cases")}</h2>
        {content.cases.length ? content.cases.map(renderCase) : <p className="saved-panel__empty">{t("saved.emptyCases", "No saved medical cases")}</p>}
      </section>
    </div>
  );
}